/**
 * Browser-safe identity and lifecycle values shared by PILLAR clients and the
 * backend. These const objects mirror the persisted Prisma enums without
 * importing the generated database client.
 */

export const Status = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    DISABLED: 'DISABLED',
    DELETED: 'DELETED',
} as const;

export type Status = keyof typeof Status;

export const InternalRole = {
    SUPERADMIN: 'SUPERADMIN',
    INTERNAL_ADMIN: 'INTERNAL_ADMIN',
} as const;

export type InternalRole = keyof typeof InternalRole;

export const OrganizationalRole = {
    PRINCIPAL: 'PRINCIPAL',
    ADMIN: 'ADMIN',
    REP: 'REP',
} as const;
export type OrganizationalRole = keyof typeof OrganizationalRole;

export const Role = {
    ...InternalRole,
    ...OrganizationalRole,
} as const;
export type Role = keyof typeof Role;

export const AllInternalRoles: Role[] = Object.values(InternalRole);
export const AllOrganizationalRoles: Role[] = Object.values(OrganizationalRole);
export const AllInternalOnly: InternalRole[] = Object.values(InternalRole);
export const AllOrganizationalOnly: OrganizationalRole[] = Object.values(OrganizationalRole);
export const AllRoles: Role[] = Object.values(Role);

export const AllAdminRoles: Role[] = [
    Role.SUPERADMIN,
    Role.INTERNAL_ADMIN,
    Role.PRINCIPAL,
    Role.ADMIN,
];
export type AdminRole = (typeof AllAdminRoles)[number];

export const RoleRankInternal = {
    SUPERADMIN: 100,
    INTERNAL_ADMIN: 90,
} as const; // Values are arbitrary and not be relied on, but the order is guaranteed to be consistent
export const RoleRankOrganizational = {
    PRINCIPAL: 80,
    ADMIN: 70,
    REP: 50,
} as const; // Values are arbitrary and not be relied on, but the order is guaranteed to be consistent

export const RoleRank = {
    ...RoleRankInternal,
    ...RoleRankOrganizational,
} as const;

export const isInternalRole = (role: Role): role is InternalRole => AllInternalRoles.includes(role);
export const isOrganizationalRole = (role: Role): role is OrganizationalRole =>
    AllOrganizationalRoles.includes(role);
export const isAdminRole = (role: Role): role is AdminRole => AllAdminRoles.includes(role);

export const UserType = {
    INTERNAL: 'internal',
    ORGANIZATIONAL: 'organizational',
    // CUSTOMER: 'customer',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];
